/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-08
 */

// Add third-party dependencies.
import { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { CloudUpload, Delete, PhotoCamera, Cancel } from '@mui/icons-material';

// Add local dependencies.
import type { Area } from 'react-easy-crop/types';
import UserAvatar from '../common/UserAvatar';
import ImageCropDialog from '../common/ImageCropDialog';
import type { User } from '../../features/auth/authApi';
import {
  createCroppedImageFile,
  createImagePreviewUrl,
  getAvatarOutputSize,
} from '../../utils/imageCrop';

/**
 * Props for the AvatarUpload component.
 */
interface AvatarUploadProps {
  /**
   * Current user object.
   */
  user?: User | null;
  /**
   * Whether the upload is in progress.
   * @default false
   */
  uploading?: boolean;
  /**
   * Upload progress percentage (0-100).
   */
  uploadProgress?: number;
  /**
   * Error message to display.
   */
  error?: string;
  /**
   * Success message to display.
   */
  success?: string;
  /**
   * Callback when a file is selected for upload.
   */
  onFileSelect?: (file: File) => void;
  /**
   * Callback when upload is cancelled.
   */
  onCancel?: () => void;
  /**
   * Callback when avatar should be deleted.
   */
  onDelete?: () => void;
  /**
   * Whether delete functionality is available.
   * @default true
   */
  allowDelete?: boolean;
  /**
   * Maximum file size in bytes.
   * @default 5242880 (5MB)
   */
  maxFileSize?: number;
  /**
   * Allowed file types.
   * @default ['image/jpeg', 'image/png', 'image/webp']
   */
  allowedTypes?: string[];
}

/**
 * AvatarUpload component that provides drag-and-drop file upload functionality
 * with image preview, validation, and progress tracking.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The avatar upload component
 *
 * @example
 * <AvatarUpload
 *   user={user}
 *   uploading={uploading}
 *   uploadProgress={progress}
 *   onFileSelect={handleFileSelect}
 *   onDelete={handleDelete}
 * />
 */
const AvatarUpload: React.FC<AvatarUploadProps> = ({
  user,
  uploading = false,
  uploadProgress = 0,
  error,
  success,
  onFileSelect,
  onCancel,
  onDelete,
  allowDelete = true,
  maxFileSize = 5242880, // 5MB
  allowedTypes = ['image/jpeg', 'image/png', 'image/webp'],
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles file selection and validation.
   */
  const handleFileSelection = useCallback(
    async (file: File) => {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        const validationError = `Invalid file type. Please select a ${allowedTypes
          .map(type => type.split('/')[1].toUpperCase())
          .join(', ')} file.`;
        console.error('File validation error:', validationError);
        return;
      }

      // Validate file size
      if (file.size > maxFileSize) {
        const maxSizeMB = Math.round(maxFileSize / 1024 / 1024);
        const validationError = `File size too large. Please select a file smaller than ${maxSizeMB}MB.`;
        console.error('File validation error:', validationError);
        return;
      }

      setSelectedFile(file);

      try {
        // Create preview URL for cropping
        const imageUrl = await createImagePreviewUrl(file);
        setCropImageUrl(imageUrl);
        setCropDialogOpen(true);
      } catch (cropError) {
        console.error('Failed to create image preview:', cropError);
      }
    },
    [maxFileSize, allowedTypes]
  );

  /**
   * Handles drag events.
   */
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  /**
   * Handles file drop.
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelection(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelection]
  );

  /**
   * Handles file input change.
   */
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileSelection(e.target.files[0]);
      }
    },
    [handleFileSelection]
  );

  /**
   * Opens the file picker.
   */
  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  /**
   * Cancels the current upload/selection.
   */
  const handleCancel = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onCancel?.();
  };

  /**
   * Handles avatar deletion.
   */
  const handleDelete = () => {
    handleCancel(); // Clear any pending uploads
    onDelete?.();
  };

  /**
   * Handles crop confirmation.
   */
  const handleCropConfirm = useCallback(
    async (croppedAreaPixels: Area) => {
      if (!selectedFile || !cropImageUrl) return;

      setIsCropping(true);
      try {
        // Create the cropped image file
        const outputSize = getAvatarOutputSize(200);
        const croppedFile = await createCroppedImageFile(
          cropImageUrl,
          croppedAreaPixels,
          `avatar-${Date.now()}`,
          outputSize,
          'image/jpeg',
          0.9
        );

        // Create preview URL for display
        const url = URL.createObjectURL(croppedFile);
        setPreviewUrl(url);

        // Close crop dialog
        setCropDialogOpen(false);
        setCropImageUrl(null);

        // Call the parent callback with the cropped file
        onFileSelect?.(croppedFile);
      } catch (cropError) {
        console.error('Failed to crop image:', cropError);
      } finally {
        setIsCropping(false);
      }
    },
    [selectedFile, cropImageUrl, onFileSelect]
  );

  /**
   * Handles crop cancellation.
   */
  const handleCropCancel = useCallback(() => {
    setCropDialogOpen(false);
    setCropImageUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * Formats file size for display.
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
      {/* Current Avatar Display */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Box sx={{ position: 'relative' }}>
          <UserAvatar user={user} size="xlarge" />
          {user?.has_avatar && allowDelete && !uploading && (
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'error.dark',
                },
              }}
              onClick={handleDelete}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Upload Area */}
      <Paper
        sx={{
          p: 3,
          border: `2px dashed ${dragActive ? 'primary.main' : 'grey.300'}`,
          borderRadius: 2,
          bgcolor: dragActive ? 'primary.50' : 'background.paper',
          cursor: uploading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: uploading ? 'grey.300' : 'primary.main',
            bgcolor: uploading ? 'background.paper' : 'primary.50',
          },
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={uploading ? undefined : openFilePicker}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {uploading ? (
            <>
              <CloudUpload sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="h6">Uploading...</Typography>
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', mt: 1 }}
                >
                  {uploadProgress}%
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <PhotoCamera sx={{ fontSize: 48, color: 'primary.main' }} />
              <Typography variant="h6" textAlign="center">
                {selectedFile ? 'File Selected' : 'Choose Avatar Photo'}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
              >
                {selectedFile
                  ? `${selectedFile.name} (${formatFileSize(selectedFile.size)})`
                  : 'Drag and drop an image here, or click to select'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                Supports JPEG, PNG, WebP • Max{' '}
                {Math.round(maxFileSize / 1024 / 1024)}MB
              </Typography>
            </>
          )}
        </Box>
      </Paper>

      {/* Action Buttons */}
      {(selectedFile || uploading) && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<Cancel />}
            onClick={handleCancel}
            disabled={uploading}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
      />

      {/* Image Crop Dialog */}
      {cropImageUrl && (
        <ImageCropDialog
          open={cropDialogOpen}
          imageUrl={cropImageUrl}
          title="Crop Avatar Photo"
          showCircularCrop
          aspect={1}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          loading={isCropping}
        />
      )}
    </Box>
  );
};

export default AvatarUpload;
